import { Table } from "antd";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useNavigate, useParams } from "react-router";
import { getCustomerMonitoring, rescanCustomer } from "../../api/monitoring";
import { showErrorToast, showSuccessToast } from "../../utils/toast";
import { usePermissionEffect } from "../../utils/usePermissionEffect";
import AlertsTable from "./AlertsTable";
import { caseColumns } from "./cases";
import { CHANNEL_LABELS, naira } from "./shared";

const ledgerColumns = [
  { title: "Date", dataIndex: "at", key: "at", render: (v) => <span className="whitespace-nowrap text-[12px]">{new Date(v).toLocaleString()}</span> },
  {
    title: "Movement",
    key: "movement",
    render: (_, e) => (
      <span className={e.direction === "in" ? "text-[#219653]" : "text-[#EB5757]"}>
        {e.direction === "in" ? "In" : "Out"} · {CHANNEL_LABELS[e.channel] || e.channel}
      </span>
    ),
  },
  {
    title: "Amount",
    key: "amount",
    render: (_, e) =>
      e.amountNgn != null ? (
        <span title={e.amountEstimated ? "Reconstructed from a legacy record" : ""}>
          {naira(e.amountNgn)}
          {e.amountEstimated ? "*" : ""}
        </span>
      ) : (
        `${e.coinQty ?? ""} ${String(e.coin || "").toUpperCase()}`
      ),
  },
  {
    title: "Counterparty",
    key: "cp",
    render: (_, e) => <span className="text-[12px] break-all">{[e.counterparty?.name, e.counterparty?.id].filter(Boolean).join(" · ") || "—"}</span>,
  },
  { title: "Status", dataIndex: "status", key: "status", render: (v) => <span className="capitalize text-[12px]">{v}</span> },
  { title: "Reference", dataIndex: "reference", key: "reference", render: (v) => <span className="font-mono text-[11px] break-all">{v}</span> },
];

const CustomerMonitoring = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { getModulePermission } = usePermissionEffect();
  const canView = getModulePermission("monitoring", "canView");
  const canWork = getModulePermission("monitoring", "canUpdate");

  const { data } = useQuery(["customer-monitoring", id], () => getCustomerMonitoring(id), { enabled: canView });
  const rescan = useMutation(() => rescanCustomer(id), {
    onSuccess: (r) => {
      showSuccessToast(r?.hits ? `${r.hits} pattern(s) found; new ones were raised as alerts` : "No patterns found");
      queryClient.invalidateQueries("monitoring-alerts");
      queryClient.invalidateQueries(["customer-monitoring", id]);
    },
    onError: (e) => showErrorToast(e.message),
  });

  if (!canView) return <div className="text-[13px] text-[#00000080]">You need the Monitoring permission to see this.</div>;
  const totals = data?.ledger?.totals || [];
  const sum = (dir) => totals.filter((t) => t.direction === dir).reduce((s, t) => s + t.amountNgn, 0);

  return (
    <div className="space-y-5">
      <div className="flex justify-between items-center">
        <div className="font-bold text-[14px]">Alerts</div>
        {canWork && (
          <button type="button" disabled={rescan.isLoading} className="text-[#4A4DE6] text-[13px] disabled:opacity-50" onClick={() => rescan.mutate()}>
            {rescan.isLoading ? "Scanning…" : "Re-run pattern checks (30 days)"}
          </button>
        )}
      </div>
      <AlertsTable fixed={{ userId: id }} defaultStatus="all" />

      {data?.cases?.length > 0 && (
        <div>
          <div className="font-bold text-[14px] mb-2">Cases</div>
          <Table
            rowKey="_id"
            size="small"
            dataSource={data.cases}
            columns={caseColumns.filter((c) => c.key !== "customer")}
            pagination={false}
            onRow={(c) => ({ onClick: () => navigate(`/monitoring/cases/${c._id}`) })}
            rowClassName="cursor-pointer"
          />
        </div>
      )}

      <div>
        <div className="font-bold text-[14px]">Money movements (last 30 days)</div>
        <div className="text-[12px] text-[#00000080] mb-2">
          In {naira(sum("in"))} · Out {naira(sum("out"))}. Every transaction type shown in one consistent format, the same
          view the monitoring rules use. * = amount reconstructed from a legacy wallet-transfer record.
        </div>
        <Table rowKey={(e) => `${e.txnId}-${e.direction}`} size="small" dataSource={data?.ledger?.entries || []} columns={ledgerColumns} pagination={{ pageSize: 15 }} />
      </div>
    </div>
  );
};

export default CustomerMonitoring;
